/**
 * Check-in API client for the business app.
 *
 * Wraps backend/app/routers/checkin.py endpoints behind the BFF proxy.
 * Covers the front-desk "mark this party arrived" flow plus the
 * pass-generation endpoints used by both staff and player surfaces.
 *
 * NOTE: staff pass REDEMPTION lives on the shared client
 * (`@bw/shared/api` `staffApi.redeemCheckin`), which hits the real
 * staff redeem route. The old local redeem helper pointed at a
 * non-existent route (404) and was removed in the clean migration —
 * do not re-add it here.
 */
import { httpClient } from "@bw/shared/api";
import type { Schema } from "@bw/shared/types/generated";

// ────────────────────────────────────────────────────────────────────
// Types — generated backend contracts
// ────────────────────────────────────────────────────────────────────

export type CheckinPass = Schema["CheckinPassResponse"];
export type CheckinStatus = Schema["CheckinStatusResponse"];
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;
export type QuickDropinCheckInRequest = DistributiveOmit<
  Schema["QuickCheckinRequest"],
  "facility_id"
>;
export type DropInSession = Schema["DropInSessionResponse"];
export type QuickDropinPaymentOptions = Schema["DropInPaymentOptionsResponse"];
export type QuickDropinPaymentMethod = Schema["SavedPaymentMethodResponse"];
export type QuickDropinPaymentMethodSetupIntent =
  Schema["PaymentMethodSetupIntentResponse"];
export type CheckinCustomerSearchResult = Schema["FacilityPlayerSearchResult"];

// ────────────────────────────────────────────────────────────────────
// Pass generation (used by client to get a QR / token for staff to scan)
// ────────────────────────────────────────────────────────────────────

export function getBookingCheckinPass(bookingId: string): Promise<CheckinPass> {
  return httpClient.get<CheckinPass>(`/checkin/bookings/${bookingId}/pass`);
}

export function getDropinCheckinPass(facilityId: string): Promise<CheckinPass> {
  return httpClient.get<CheckinPass>(`/checkin/dropin/${facilityId}/pass`);
}

// NOTE (frictionless drop-in, 2026-07-02): getAssignmentCheckinPass +
// getQueueEntryCheckinPass were REMOVED — they pointed at the deleted
// GET /checkin/assignments/{id}/pass and GET /checkin/queue/{entry}/pass routes
// (the on-court QR pass concept is gone) and had no callers. Clean migration:
// no dead client fn left pointing at a 404.

// ────────────────────────────────────────────────────────────────────
// Staff actions
// ────────────────────────────────────────────────────────────────────

/**
 * Quick drop-in check-in: staff confirms a walk-in player has arrived
 * and (optionally) charged a single drop-in.
 */
export function quickDropinCheckin(
  facilityId: string,
  req: QuickDropinCheckInRequest,
): Promise<DropInSession> {
  return httpClient.post<DropInSession>("/checkin/dropin", {
    ...req,
    facility_id: facilityId,
  });
}

export function getQuickDropinPaymentOptions(
  facilityId: string,
  playerUserId: string,
): Promise<QuickDropinPaymentOptions> {
  const params = new URLSearchParams({
    facility_id: facilityId,
    player_user_id: playerUserId,
  });
  return httpClient.get<QuickDropinPaymentOptions>(
    `/checkin/dropin/payment-options?${params.toString()}`,
  );
}

export function getQuickDropinPaymentMethods(
  facilityId: string,
  playerUserId: string,
): Promise<QuickDropinPaymentMethod[]> {
  const params = new URLSearchParams({
    facility_id: facilityId,
    player_user_id: playerUserId,
  });
  return httpClient.get<QuickDropinPaymentMethod[]>(
    `/checkin/dropin/payment-methods?${params.toString()}`,
  );
}

export function createQuickDropinPaymentMethodSetupIntent(
  facilityId: string,
  playerUserId: string,
  idempotencyKey: string,
): Promise<QuickDropinPaymentMethodSetupIntent> {
  return httpClient.post<QuickDropinPaymentMethodSetupIntent>(
    "/checkin/dropin/payment-methods/setup-intent",
    {
      facility_id: facilityId,
      player_user_id: playerUserId,
      idempotency_key: idempotencyKey,
    },
  );
}

export function searchCheckinCustomers(
  facilityId: string,
  query: string,
): Promise<CheckinCustomerSearchResult[]> {
  const params = new URLSearchParams({ q: query });
  return httpClient.get<CheckinCustomerSearchResult[]>(
    `/facilities/${facilityId}/players/search?${params.toString()}`,
  );
}

/**
 * Get the current check-in status of the authenticated user (used by the
 * player app; provided here for symmetry / future admin tools).
 */
export function getCheckinStatus(): Promise<CheckinStatus> {
  return httpClient.get<CheckinStatus>("/checkin/status");
}
